import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in or create an account | RoleBoard" },
      {
        name: "description",
        content:
          "Sign in to RoleBoard or create a free account to start tracking auditions and self tapes.",
      },
      { property: "og:title", content: "Sign in to RoleBoard" },
      {
        property: "og:description",
        content: "Sign in or create a free RoleBoard account.",
      },
      { property: "og:url", content: "https://roleboard.app/login" },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/login" }],
  }),
  component: LoginPage,
});

const schema = z.object({
  email: z.string().trim().email("Enter a valid email").max(255),
  password: z.string().min(6, "At least 6 characters").max(72),
});

function LoginPage() {
  const [mode, setMode] = useState<"signin" | "signup">("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  async function onGoogleSignIn() {
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: `${window.location.origin}/tapes` },
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error("Google sign-in failed. Try again.");
      setGoogleLoading(false);
    }
  }

  useEffect(() => {
    if (!loading && user) navigate({ to: "/tapes", search: {} as never });
  }, [user, loading, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    if (mode === "signup" && password !== confirmPassword) {
      setConfirmError("Passwords don't match");
      return;
    }
    setConfirmError(null);
    setSubmitting(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email: parsed.data.email,
          password: parsed.data.password,
          options: { emailRedirectTo: `${window.location.origin}/` },
        });
        if (error) throw error;
        // Try to log in immediately (works when email confirmation is disabled)
        const { error: signInErr } = await supabase.auth.signInWithPassword({
          email: parsed.data.email,
          password: parsed.data.password,
        });
        if (signInErr) {
          toast.success("Account created. Please confirm your email, then log in.");
          setMode("signin");
        } else {
          toast.success("Welcome. Your workspace is ready.");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: parsed.data.email,
          password: parsed.data.password,
        });
        if (error) throw error;
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isSignup = mode === "signup";

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-hidden">
      {/* Ambient gradient backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, color-mix(in oklab, var(--primary) 22%, transparent) 0%, transparent 70%), radial-gradient(40% 35% at 80% 90%, color-mix(in oklab, var(--primary) 14%, transparent) 0%, transparent 70%)",
        }}
      />

      <div className="min-h-screen grid place-items-center px-5 py-10">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="flex flex-col items-center mb-1"
            aria-label="RoleBoard home"
          >
            <BrandLogo className="h-20 md:h-24 w-auto" />
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-semibold tracking-tight leading-tight md:text-3xl">
              RoleBoard
            </h1>
            <p className="mt-3 text-[15px] text-muted-foreground max-w-sm mx-auto">
              All your auditions. In one place.
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card/60 backdrop-blur-xl p-6 shadow-xl shadow-black/20">
            <Button
              type="button"
              variant="outline"
              onClick={onGoogleSignIn}
              disabled={googleLoading || submitting}
              className="w-full h-10 mb-4"
            >
              {googleLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Redirecting…
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
                    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.66 4.1-5.5 4.1-3.31 0-6-2.74-6-6.1s2.69-6.1 6-6.1c1.88 0 3.14.8 3.86 1.49l2.63-2.54C16.84 3.39 14.66 2.4 12 2.4 6.85 2.4 2.7 6.55 2.7 11.7s4.15 9.3 9.3 9.3c5.37 0 8.92-3.77 8.92-9.07 0-.61-.07-1.08-.16-1.55H12z"/>
                    <path fill="#4285F4" d="M21.6 12.23c0-.69-.06-1.36-.18-2H12v3.78h5.39c-.23 1.24-.93 2.29-1.98 3v2.49h3.2c1.87-1.72 2.99-4.27 2.99-7.27z"/>
                    <path fill="#FBBC05" d="M5.84 13.74A5.99 5.99 0 0 1 5.5 12c0-.6.1-1.18.27-1.74V7.78H2.54A9.3 9.3 0 0 0 1.5 12c0 1.5.36 2.92 1.04 4.22l3.3-2.48z"/>
                    <path fill="#34A853" d="M12 21c2.7 0 4.96-.89 6.61-2.42l-3.2-2.49c-.88.6-2.05.96-3.41.96-2.62 0-4.84-1.77-5.63-4.15l-3.3 2.48C4.7 18.86 8.06 21 12 21z"/>
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>

            <div className="flex items-center gap-3 mb-4">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted/60 mb-5">
              <button
                type="button"
                onClick={() => setMode("signup")}
                className={`text-sm font-medium py-1.5 rounded-md transition-colors ${
                  isSignup
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Sign up
              </button>
              <button
                type="button"
                onClick={() => setMode("signin")}
                className={`text-sm font-medium py-1.5 rounded-md transition-colors ${
                  !isSignup
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Log in
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between min-h-5">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    aria-hidden={isSignup}
                    tabIndex={isSignup ? -1 : 0}
                    className={`text-xs text-muted-foreground hover:text-foreground transition-opacity ${
                      isSignup ? "pointer-events-none opacity-0" : "opacity-100"
                    }`}
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {isSignup && (
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (confirmError) setConfirmError(null);
                    }}
                    required
                    aria-invalid={!!confirmError}
                    aria-describedby={confirmError ? "confirm-password-error" : undefined}
                  />
                  {confirmError && (
                    <p id="confirm-password-error" className="text-xs text-destructive">
                      {confirmError}
                    </p>
                  )}
                </div>
              )}
              <Button
                type="submit"
                className="w-full h-10"
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : isSignup ? (
                  "Create account"
                ) : (
                  "Log in"
                )}
              </Button>
            </form>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-6">
            By continuing, you agree to our{" "}
            <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
              Privacy Policy
            </Link>
            .
          </p>
          <p className="text-xs text-muted-foreground text-center mt-3">
            RoleBoard Beta 1.0 2026
          </p>
        </div>
      </div>
    </div>
  );
}
